import { dbService } from '../firebaseInstance';

const initialActiveSession = {
  userID: null,
  token: null,
  generation: 0,
  status: 'idle'
};

let activeSession = { ...initialActiveSession };

const readLocalStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (_) {
    return null;
  }
};

const writeLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn(`Failed to persist ${key}`, error);
    throw error;
  }
};

const removeLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (_) {}
};

const updateActiveSession = (next) => {
  activeSession = {
    ...activeSession,
    ...next,
    generation: activeSession.generation + 1,
  };
  return { ...activeSession };
};

const createTokenCandidateError = (message, code) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

export const getActiveSession = () => ({ ...activeSession });

/**
 * Runtime API calls should read the already-adopted token first. localStorage
 * remains a compatibility cache while older call sites are migrated.
 */
export const getManagedActiveToken = () =>
  activeSession.token ||
  readLocalStorage('longLivedToken') ||
  readLocalStorage('accessToken');

export const setActiveSessionStatus = (status) => {
  activeSession = { ...activeSession, status };
  return { ...activeSession };
};

/**
 * Persist a token only after /me validated it for the expected user.
 * This intentionally does not write Firestore or extend tokenExpiry.
 */
export const adoptValidatedToken = ({ userID, token }) => {
  if (!userID || !token) {
    throw new Error('A user ID and validated token are required');
  }

  writeLocalStorage('longLivedToken', token);
  writeLocalStorage('userID', String(userID));

  return updateActiveSession({
    userID: String(userID),
    token,
    status: 'ready',
  });
};

/** Clear only this browser session. Remote invalidation is a separate action. */
export const clearLocalSession = () => {
  removeLocalStorage('accessToken');
  removeLocalStorage('longLivedToken');
  removeLocalStorage('threads_token');
  removeLocalStorage('userID');

  return updateActiveSession({
    userID: null,
    token: null,
    status: 'reauth_required',
  });
};

/**
 * 토큰 저장 (기존 시스템과 호환성 유지)
 * @param {string} userID - 사용자 ID
 * @param {string} accessToken - 단기 액세스 토큰
 * @param {string} longLivedToken - 장기 토큰 (60일)
 */
export const saveTokens = async (userID, accessToken, longLivedToken) => {
  try {
    console.log('🔑 Saving tokens for user:', userID);
    
    // Firestore에 토큰 정보 저장 (userID를 문자열로 변환)
    await dbService.collection("user").doc(String(userID)).set({
      accessToken: accessToken,
      longLivedToken: longLivedToken,
      tokenExpiry: Date.now() + (60 * 24 * 60 * 60 * 1000), // 60일
      lastTokenRefresh: Date.now(),
      tokenStatus: 'active'
    }, { merge: true });
    
    // 기존 시스템 호환성 유지 - localStorage 저장
    writeLocalStorage("accessToken", accessToken);
    adoptValidatedToken({ userID, token: longLivedToken });
    
    console.log('✅ Tokens saved successfully');
    return true;
    
  } catch (error) {
    console.error('❌ Token saving failed:', error);
    throw error;
  }
};

/**
 * Fetch the canonical stored token without making it active. Callers must
 * validate the candidate against /me before adoptValidatedToken().
 */
export const getTokenCandidate = async (userID) => {
  if (!userID) {
    const localToken = getManagedActiveToken();
    return localToken
      ? { userID: null, token: localToken, source: 'local-cache' }
      : null;
  }

  const userDoc = await dbService.collection("user").doc(String(userID)).get();

  if (!userDoc.exists) {
    throw createTokenCandidateError('User document not found', 'TOKEN_CANDIDATE_NOT_FOUND');
  }

  const userData = userDoc.data();
  const { longLivedToken, tokenExpiry, tokenStatus } = userData;

  if (!longLivedToken) {
    throw createTokenCandidateError('No long-lived token found', 'TOKEN_CANDIDATE_NOT_FOUND');
  }

  if (tokenStatus === 'invalidated') {
    throw createTokenCandidateError('Long-lived token has been invalidated', 'TOKEN_CANDIDATE_INVALID');
  }

  if (tokenExpiry && Date.now() > tokenExpiry) {
    throw createTokenCandidateError('Long-lived token has expired', 'TOKEN_CANDIDATE_EXPIRED');
  }

  return {
    userID: String(userID),
    token: longLivedToken,
    expiresAt: tokenExpiry || null,
    tokenStatus: tokenStatus || 'active',
    source: 'firestore',
  };
};

/**
 * 현재 long-lived token 반환 (갱신 없이)
 * @param {string} userID - 사용자 ID
 * @returns {string} long-lived token
 */
export const getLongLivedToken = async (userID) => {
  try {
    const candidate = await getTokenCandidate(userID);
    console.log('✅ Long-lived token is valid');
    return candidate.token;
    
  } catch (error) {
    console.error('❌ Get long-lived token failed:', error);
    throw error;
  }
};

/**
 * 현재 유효한 long-lived token 가져오기 (갱신 없이 단순 반환)
 * @param {string} userID - 사용자 ID
 * @returns {string|null} 유효한 토큰 또는 null
 */
export const getCurrentToken = async (userID) => {
  try {
    const candidate = await getTokenCandidate(userID);
    return candidate?.token || null;
    
  } catch (error) {
    console.error('❌ Get current token failed:', error);
    // 만료된 경우 null 반환
    return null;
  }
};

/**
 * 토큰 무효화 (로그아웃 시 사용)
 * @param {string} userID - 사용자 ID
 */
export const invalidateTokens = async (userID) => {
  try {
    console.log('🔑 Invalidating tokens for user:', userID);
    
    // Firestore 업데이트
    if (userID) {
      await dbService.collection("user").doc(userID).update({
        tokenStatus: 'invalidated',
        invalidatedAt: Date.now()
      });
    }
    
    // localStorage 정리
    clearLocalSession();
    
    console.log('✅ Tokens invalidated successfully');
    
  } catch (error) {
    console.error('❌ Token invalidation failed:', error);
    // 실패해도 localStorage는 정리
    clearLocalSession();
  }
};

/**
 * 토큰 상태 확인 (갱신 로직 없이 단순 상태만)
 * @param {string} userID - 사용자 ID
 * @returns {Object} 토큰 상태 정보
 */
export const getTokenStatus = async (userID) => {
  try {
    if (!userID) {
      return { status: 'no_user', valid: false };
    }
    
    const userDoc = await dbService.collection("user").doc(userID).get();
    
    if (!userDoc.exists) {
      return { status: 'user_not_found', valid: false };
    }
    
    const userData = userDoc.data();
    const { longLivedToken, tokenExpiry, tokenStatus } = userData;
    
    if (!longLivedToken) {
      return { status: 'no_token', valid: false };
    }
    
    const isExpired = !!tokenExpiry && Date.now() > tokenExpiry;
    const isInvalidated = tokenStatus === 'invalidated';
    
    return {
      status: tokenStatus || 'active',
      valid: !isExpired && !isInvalidated,
      expiresAt: tokenExpiry ? new Date(tokenExpiry) : null,
      daysUntilExpiry: tokenExpiry
        ? Math.floor((tokenExpiry - Date.now()) / (24 * 60 * 60 * 1000))
        : null
    };
    
  } catch (error) {
    console.error('❌ Get token status failed:', error);
    return { status: 'error', valid: false, error: error.message };
  }
};

// Keep already-open tabs aligned after another tab adopts or clears a token.
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('storage', (event) => {
    if (event.key !== 'longLivedToken' && event.key !== 'userID') return;

    const token = readLocalStorage('longLivedToken');
    const userID = readLocalStorage('userID');
    updateActiveSession({
      token,
      userID,
      status: token && userID ? 'ready' : 'idle',
    });
  });
}

/**
 * Long-lived 토큰 교환 (초기 로그인 시 사용)
 * @param {string} shortLivedToken - 단기 토큰
 * @param {string} clientSecret - 앱 시크릿
 * @returns {Object} 장기 토큰 정보
 */
export const exchangeForLongLivedToken = async (shortLivedToken, clientSecret, clientId) => {
  try {
    console.log('🔄 Exchanging for long-lived token...');
    
    const response = await fetch('https://graph.threads.net/access_token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `grant_type=th_exchange_token&client_id=${encodeURIComponent(clientId)}&client_secret=${encodeURIComponent(clientSecret)}&access_token=${encodeURIComponent(shortLivedToken)}`
    });
    
    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    console.log('✅ Long-lived token obtained successfully');
    return {
      access_token: data.access_token,
      expires_in: data.expires_in || (60 * 24 * 60 * 60) // 기본 60일
    };
    
  } catch (error) {
    console.error('❌ Token exchange failed:', error);
    throw error;
  }
};
